import type {
  AdvanceRecoupmentScope,
  AdvanceRecoupmentTarget,
  Expense,
  Deduction,
  DistributionRule,
  DistributionRuleInput,
  DistributionRuleMember,
  Income,
  Project,
  SettlementCategory,
  SettlementThresholdTier,
  TaxRoundingMode,
} from '../types';
import { roundCurrencyAmount } from './utils';

export function ruleProjectId(rule: Partial<DistributionRule>): string | null {
  return rule.project_id ?? rule.conditions?.project_ids?.[0] ?? null;
}

export function normalizeRuleInput<T extends Partial<DistributionRuleInput>>(data: T): T {
  if (data.project_id === undefined) return data;
  return {
    ...data,
    conditions: {
      ...(data.conditions ?? {}),
      project_ids: data.project_id ? [data.project_id] : [],
    },
  };
}

export function normalizeDistributionRoundingMode(value?: string | null): TaxRoundingMode {
  return value === 'round' || value === 'ceil' || value === 'floor' ? value : 'floor';
}

export function calculateRoundedRatioDistributions<T extends {
  basis?: string;
  type?: string;
  ratio?: number;
  rounding_mode?: string;
  rounding_adjustment_target?: boolean;
}>(
  members: T[],
  gross: number,
  distributable: number,
): Map<T, number> {
  const amounts = new Map<T, number>();
  const ratioGroups = new Map<string, T[]>();

  members.forEach(member => {
    if ((member.type ?? 'ratio') !== 'ratio') return;
    const basis = member.basis ?? 'net';
    const group = ratioGroups.get(basis) ?? [];
    group.push(member);
    ratioGroups.set(basis, group);
  });

  ratioGroups.forEach(group => {
    const basis = group[0]?.basis ?? 'net';
    const base = basis === 'gross' ? gross : distributable;
    const totalRatio = group.reduce((sum, member) => sum + (Number.isFinite(member.ratio) ? member.ratio ?? 0 : 0), 0);
    const targetTotal = Math.round(base * totalRatio);

    let roundedTotal = 0;
    group.forEach(member => {
      const rawAmount = base * (member.ratio ?? 0);
      const roundedAmount = roundCurrencyAmount(rawAmount, normalizeDistributionRoundingMode(member.rounding_mode));
      amounts.set(member, roundedAmount);
      roundedTotal += roundedAmount;
    });

    const adjustment = targetTotal - roundedTotal;
    if (adjustment === 0) return;
    const target = group.find(member => member.rounding_adjustment_target);
    if (!target) return;
    amounts.set(target, (amounts.get(target) ?? 0) + adjustment);
  });

  return amounts;
}

export function normalizeSettlementItem(item: Deduction): Deduction {
  const category = item.category ?? 'other';
  const accounting_treatment = item.accounting_treatment ?? 'expense';
  const payee_type = item.payee_type ?? 'none';
  const creates_payment = item.creates_payment ?? (category !== 'advance' && payee_type !== 'none');
  return {
    ...item,
    category,
    affects_distribution: item.affects_distribution ?? true,
    accounting_treatment,
    payee_type,
    payee_id: item.payee_id ?? null,
    creates_payment,
    report_target: item.report_target ?? (category === 'advance' || creates_payment),
    cap_to_available: item.cap_to_available ?? (category === 'top_off_expense' || category === 'advance'),
    valid_until: item.valid_until ?? null,
    advance_recoupment_target: item.advance_recoupment_target ?? 'all',
    advance_recoupment_scope: item.advance_recoupment_scope ?? 'rule',
    condition_type: item.condition_type ?? 'none',
    threshold_amount: item.threshold_amount ?? 0,
    threshold_mode: item.threshold_mode ?? 'trigger_all',
    threshold_tiers: normalizeSettlementThresholdTiers(item.threshold_tiers),
    active_date_from: item.active_date_from ?? null,
    active_date_to: item.active_date_to ?? null,
    date_basis: item.date_basis ?? 'transaction_date',
  };
}

export function normalizeSettlementThresholdTiers(value: unknown): SettlementThresholdTier[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(item => {
      if (!item || typeof item !== 'object') return null;
      const raw = item as Record<string, unknown>;
      const threshold = Number(raw.threshold_amount ?? 0);
      const rate = raw.rate === undefined || raw.rate === null || raw.rate === '' ? undefined : Number(raw.rate);
      const amount = raw.amount === undefined || raw.amount === null || raw.amount === '' ? undefined : Number(raw.amount);
      return {
        threshold_amount: Number.isFinite(threshold) ? Math.max(0, threshold) : 0,
        ...(rate !== undefined && Number.isFinite(rate) ? { rate } : {}),
        ...(amount !== undefined && Number.isFinite(amount) ? { amount: Math.max(0, amount) } : {}),
      };
    })
    .filter((item): item is SettlementThresholdTier => item !== null)
    .sort((a, b) => a.threshold_amount - b.threshold_amount);
}

export function getProjectIds(projectId: string, projects: Project[], visited = new Set<string>()): string[] {
  if (!projectId || visited.has(projectId)) return [];
  visited.add(projectId);
  const ids = [projectId];
  const children = projects.filter(p => p.parent_id === projectId);
  for (const child of children) {
    ids.push(...getProjectIds(child.id, projects, visited));
  }
  return ids;
}

export function isProjectDescendantOf(projectId: string, ancestorId: string, projects: Project[]): boolean {
  if (projectId === ancestorId) return true;
  let current = projects.find(project => project.id === projectId);
  const visited = new Set<string>([projectId]);
  while (current?.parent_id) {
    if (current.parent_id === ancestorId) return true;
    if (visited.has(current.parent_id)) return false;
    visited.add(current.parent_id);
    current = projects.find(project => project.id === current?.parent_id);
  }
  return false;
}

export function getRuleProjectIds(rule: Partial<DistributionRule>, projects: Project[]): string[] {
  const primaryProjectId = ruleProjectId(rule);
  const explicitProjectIds = primaryProjectId ? [primaryProjectId] : (rule.conditions?.project_ids ?? []);
  return explicitProjectIds.flatMap(projectId => getProjectIds(projectId, projects));
}

export function ruleHasDistributionMembers(ruleId: string, ruleMembers: DistributionRuleMember[]): boolean {
  return ruleMembers.some(member => member.rule_id === ruleId);
}

export function getOwnedProjectIdsForRule(
  rule: DistributionRule,
  rules: DistributionRule[],
  ruleMembers: DistributionRuleMember[],
  projects: Project[]
): string[] {
  const scopedIds = getRuleProjectIds(rule, projects);
  const ownerProjectId = ruleProjectId(rule);
  if (!ownerProjectId) return scopedIds;

  const delegatedIds = new Set<string>();
  for (const candidate of rules) {
    if (candidate.id === rule.id) continue;
    if (!ruleHasDistributionMembers(candidate.id, ruleMembers)) continue;
    const candidateProjectId = ruleProjectId(candidate);
    if (!candidateProjectId || candidateProjectId === ownerProjectId) continue;
    if (!isProjectDescendantOf(candidateProjectId, ownerProjectId, projects)) continue;
    for (const projectId of getProjectIds(candidateProjectId, projects)) {
      delegatedIds.add(projectId);
    }
  }

  return scopedIds.filter(projectId => !delegatedIds.has(projectId));
}

export function getRollupSettlementRules(
  ownerRule: DistributionRule,
  rules: DistributionRule[],
  ruleMembers: DistributionRuleMember[],
  projects: Project[]
): DistributionRule[] {
  const ownerProjectId = ruleProjectId(ownerRule);
  if (!ownerProjectId || !ruleHasDistributionMembers(ownerRule.id, ruleMembers)) return [];
  const ownedIds = new Set(getOwnedProjectIdsForRule(ownerRule, rules, ruleMembers, projects));

  return rules.filter(candidate => {
    if (candidate.id === ownerRule.id) return false;
    if (ruleHasDistributionMembers(candidate.id, ruleMembers)) return false;
    const candidateProjectId = ruleProjectId(candidate);
    if (!candidateProjectId) return false;
    return ownedIds.has(candidateProjectId) && isProjectDescendantOf(candidateProjectId, ownerProjectId, projects);
  });
}

interface SettlementConditionContext {
  incomes?: Income[];
  expenses?: Expense[];
  priorDeductionAmount?: number;
}

function filterBySettlementActivePeriod<T extends { date: string }>(items: T[], item: Deduction): T[] {
  const dateFrom = item.active_date_from;
  const dateTo = item.active_date_to;
  return items.filter(row => {
    if (dateFrom && row.date < dateFrom) return false;
    if (dateTo && row.date > dateTo) return false;
    return true;
  });
}

export function settlementRawAmount(
  item: Deduction,
  gross: number,
  netBeforeSettlement: number,
  context: SettlementConditionContext = {}
): {
  base: number;
  claim: number;
  condition_base_amount: number;
  threshold_excess_amount: number;
  condition_met: boolean;
  applied_rate: number;
  applied_amount: number;
  effective_base: number;
  applied_tier_threshold_amount?: number;
} {
  const hasActivePeriod = Boolean(item.active_date_from || item.active_date_to);
  const scopedIncomes = hasActivePeriod ? filterBySettlementActivePeriod(context.incomes ?? [], item) : (context.incomes ?? []);
  const scopedExpenses = hasActivePeriod ? filterBySettlementActivePeriod(context.expenses ?? [], item) : (context.expenses ?? []);
  const periodGross = hasActivePeriod
    ? scopedIncomes.reduce((sum, income) => sum + income.amount, 0)
    : gross;
  const periodNet = hasActivePeriod
    ? periodGross - scopedExpenses.reduce((sum, expense) => sum + expense.amount, 0)
    : netBeforeSettlement;

  const priorDeductionAmount = Math.max(0, context.priorDeductionAmount ?? 0);
  const base = item.basis === 'gross'
    ? periodGross
    : item.basis === 'gross_after_prior_deductions'
    ? Math.max(0, periodGross - priorDeductionAmount)
    : periodNet;
  const conditionType = item.condition_type ?? 'none';
  const thresholdAmount = Math.max(0, item.threshold_amount ?? 0);
  const conditionBase = conditionType === 'gross_threshold'
    ? periodGross
    : conditionType === 'distribution_threshold'
    ? periodNet
    : base;
  const conditionMet = conditionType === 'none' || conditionBase > thresholdAmount;
  const thresholdExcess = conditionType === 'none' ? 0 : Math.max(0, conditionBase - thresholdAmount);
  const matchedTiers = normalizeSettlementThresholdTiers(item.threshold_tiers)
    .filter(tier => conditionBase > tier.threshold_amount);
  const appliedTier = matchedTiers[matchedTiers.length - 1];
  const appliedRate = item.type === 'ratio'
    ? appliedTier?.rate ?? item.rate
    : item.rate;
  const appliedAmount = item.type === 'fixed'
    ? appliedTier?.amount ?? item.amount
    : item.amount;
  const effectiveBase = !conditionMet
    ? 0
    : conditionType !== 'none' && item.threshold_mode === 'excess_only'
    ? Math.max(0, base - thresholdAmount)
    : base;
  const conditionalRaw = !conditionMet
    ? 0
    : item.type === 'ratio'
    ? Math.floor(effectiveBase * appliedRate)
    : appliedAmount;
  return {
    base,
    effective_base: effectiveBase,
    claim: Math.max(0, conditionalRaw),
    condition_base_amount: conditionBase,
    threshold_excess_amount: thresholdExcess,
    condition_met: conditionMet,
    applied_rate: appliedRate,
    applied_amount: appliedAmount,
    applied_tier_threshold_amount: appliedTier?.threshold_amount,
  };
}

export function isSettlementActive(item: Deduction, dateFrom?: string): boolean {
  return !item.valid_until || !dateFrom || dateFrom <= item.valid_until;
}

export function payeeKey(payeeType?: string, payeeId?: string | null): string | null {
  if (!payeeType || payeeType === 'none' || !payeeId) return null;
  if (payeeType === 'member' || payeeType === 'counterparty') return `party:${payeeId}`;
  return `${payeeType}:${payeeId}`;
}

type AdvancePaymentKind = 'member_distribution' | 'settlement_payment';

interface AdvanceTracker {
  id: string;
  rule_id: string;
  project_id: string | null;
  project_scope_ids: string[];
  balance: number;
  recouped: number;
  target: AdvanceRecoupmentTarget;
  scope: AdvanceRecoupmentScope;
}

function advanceMatchesTarget(
  target: AdvanceRecoupmentTarget,
  kind: AdvancePaymentKind,
  category?: SettlementCategory
): boolean {
  switch (target ?? 'all') {
    case 'all':
      return true;
    case 'member_distribution':
      return kind === 'member_distribution';
    case 'settlement_payment':
      return kind === 'settlement_payment';
    case 'royalty':
      return kind === 'settlement_payment' && category === 'royalty';
    case 'publishing_royalty':
      return kind === 'settlement_payment' && category === 'publishing_royalty';
    case 'royalty_and_publishing':
      return kind === 'settlement_payment' && (category === 'royalty' || category === 'publishing_royalty');
  }
}

function rulePrimaryProjectId(rule: DistributionRule): string | null {
  return rule.project_id ?? rule.conditions?.project_ids?.[0] ?? null;
}

function advanceScopeProjectIds(scope: AdvanceRecoupmentScope, sourceProjectId: string | null, projects: Project[]): string[] {
  if (!sourceProjectId || scope === 'rule' || scope === 'all_projects') return [];
  const sourceProject = projects.find(p => p.id === sourceProjectId);
  if (scope === 'parent_project_tree') {
    const rootId = sourceProject?.parent_id ?? sourceProjectId;
    return getProjectIds(rootId, projects);
  }
  return getProjectIds(sourceProjectId, projects);
}

function advanceMatchesScope(advance: AdvanceTracker, ruleId: string, projectId: string | null): boolean {
  if (advance.scope === 'all_projects') return true;
  if (advance.scope === 'rule') return advance.rule_id === ruleId;
  if (!projectId) return false;
  return advance.project_scope_ids.includes(projectId);
}

export function applyAdvanceOffset(
  advances: Map<string, AdvanceTracker[]>,
  payeeType: string,
  payeeId: string | null,
  amount: number,
  payment: { kind: AdvancePaymentKind; category?: SettlementCategory; ruleId: string; projectId: string | null }
): { advance_offset: number; payable_amount: number } {
  const key = payeeKey(payeeType, payeeId);
  if (!key) return { advance_offset: 0, payable_amount: amount };
  const payeeAdvances = advances.get(key) ?? [];
  if (payeeAdvances.length === 0 || amount <= 0) {
    return { advance_offset: 0, payable_amount: amount };
  }
  let remaining = amount;
  let advance_offset = 0;
  for (const advance of payeeAdvances) {
    if (remaining <= 0) break;
    if (advance.balance <= 0) continue;
    if (!advanceMatchesScope(advance, payment.ruleId, payment.projectId)) continue;
    if (!advanceMatchesTarget(advance.target, payment.kind, payment.category)) continue;
    const offset = Math.min(remaining, advance.balance);
    advance.balance -= offset;
    advance.recouped += offset;
    remaining -= offset;
    advance_offset += offset;
  }
  return { advance_offset, payable_amount: remaining };
}

export function buildAdvanceTrackers(
  rules: DistributionRule[],
  deductions: Deduction[],
  projects: Project[],
  dateFrom?: string
): Map<string, AdvanceTracker[]> {
  const advances = new Map<string, AdvanceTracker[]>();
  const ruleMap = new Map(rules.map(rule => [rule.id, rule]));
  for (const item of deductions.filter(d => d.category === 'advance' && isSettlementActive(d, dateFrom))) {
    const key = payeeKey(item.payee_type, item.payee_id);
    const rule = ruleMap.get(item.rule_id);
    if (!key || !rule) continue;
    const projectId = rulePrimaryProjectId(rule);
    const scope = item.advance_recoupment_scope ?? 'rule';
    const current = advances.get(key) ?? [];
    current.push({
      id: item.id,
      rule_id: item.rule_id,
      project_id: projectId,
      project_scope_ids: advanceScopeProjectIds(scope, projectId, projects),
      balance: item.amount,
      recouped: 0,
      target: item.advance_recoupment_target ?? 'all',
      scope,
    });
    advances.set(key, current);
  }
  return advances;
}
